import { useLocation } from "react-router-dom";
import { IoMail } from "react-icons/io5";
import { PiPhoneCallFill } from "react-icons/pi";
import { BiLogoInstagramAlt, BiLogoFacebookCircle } from "react-icons/bi";
import { IoLogoWhatsapp } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import logo from "../../../assets/images/logo.png";
import { FaCcVisa, FaCcMastercard, FaCcApplePay } from "react-icons/fa";
import { SiGooglepay } from "react-icons/si";

export const Footer = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const savedLng = localStorage.getItem("i18nextLngMainSiteViArt") || "en";
  const currentYear = new Date().getFullYear();

  const openWhatsApp = () => {
    window.location.href = "https://wa.me/+37499010088";
  };

  const isHome = location.pathname === "/";

  const socialLinks = [
    {
      href: "https://www.instagram.com/viart.am/",
      icon: <BiLogoInstagramAlt className="w-4 h-4" />,
      label: "Instagram",
    },
    {
      href: "https://www.facebook.com/people/viartam/61580436554420/",
      icon: <BiLogoFacebookCircle className="w-4 h-4" />,
      label: "Facebook",
    },
  ];

  return (
    <footer className="relative bg-[#030014] border-t border-white/5 overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute bottom-0 left-[-5%] w-[500px] h-[350px] bg-purple-600/6 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-0 right-[-5%] w-[400px] h-[300px] bg-indigo-600/6 rounded-full blur-[100px] pointer-events-none" />

      {/* Noise texture */}
      <div
        className="absolute inset-0 opacity-[0.025] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">

        {/* ── Main Grid ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">

          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="flex flex-col gap-5"
          >
            {/* Logo + wordmark */}
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                <img src={logo} alt="VIART logo" className="w-6 h-auto object-contain" />
              </div>
              <span className="text-white font-bold tracking-[0.3em] text-sm uppercase">
                VIART
              </span>
            </div>

            <p className="text-sm text-gray-400/80 leading-relaxed font-light max-w-xs">
              {t("globals.footer-tagline", "Premium digital business cards crafted to make your business connections easy.")}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-2 mt-1">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="group flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-white hover:border-purple-500/40 hover:bg-purple-500/10 transition-all duration-300"
                >
                  {s.icon}
                </a>
              ))}
              <button
                onClick={openWhatsApp}
                aria-label="WhatsApp"
                className="group flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.03] border border-white/[0.06] text-gray-400 hover:text-white hover:border-purple-500/40 hover:bg-purple-500/10 transition-all duration-300"
              >
                <IoLogoWhatsapp className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Spacer column (center) — empty on mobile, used for layout on larger screens */}
          <div className="hidden md:block" />

          {/* Contact Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
            className="flex flex-col gap-5"
          >
            {/* Section label */}
            <div className="flex items-center gap-3">
              <div className="h-[2px] w-8 bg-gradient-to-r from-purple-500 to-transparent" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400">
                {t("globals.contact-us")}
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <a
                href="tel:+37499010088"
                className="group flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.06] group-hover:border-purple-500/30 transition-colors duration-300">
                  <PiPhoneCallFill className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-light">+374 (99) 01-00-88</span>
              </a>

              <div className="flex items-center gap-3 text-gray-400">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <IoMail className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm font-light">info@viart.am</span>
              </div>
            </div>

            {/* Address */}
            <p className="text-xs text-gray-500/80 font-light leading-relaxed mt-1">
              {t("globals.business-address")}
            </p>
          </motion.div>

        </div>

        {/* ── Divider ────────────────────────────────────────────── */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/[0.08] to-transparent mb-8" />

        {/* ── Bottom Bar ─────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-between gap-4 text-[10px] text-gray-500/70"
        >
          <p>© {currentYear} VIART™. {t("globals.all-rights-reserved")}</p>

          {/* Payment methods */}
          <div className="flex items-center gap-1.5 order-first sm:order-none">
            {([
              { Icon: FaCcVisa, label: 'Visa' },
              { Icon: FaCcMastercard, label: 'Mastercard' },
              { Icon: FaCcApplePay, label: 'Apple Pay' },
              { Icon: SiGooglepay, label: 'Google Pay' },
            ] as const).map(({ Icon, label }) => (
              <div
                key={label}
                title={label}
                className="flex items-center justify-center w-12 h-8 bg-white/[0.03] border border-white/[0.06] rounded-md hover:border-purple-500/30 hover:bg-white/[0.06] transition-colors duration-200"
              >
                <Icon className="text-xl text-gray-400" />
              </div>
            ))}
          </div>

          <div className="flex items-center text-center gap-4">
            <a
              href={`/privacy-policy/${savedLng === "am" ? "" : savedLng}`}
              className="hover:text-gray-300 transition-colors duration-200"
            >
              {t("globals.privacy-policy")}
            </a>
            <span className="text-white/10">|</span>
            <a
              href={`/terms-and-conditions/${savedLng === "am" ? "" : savedLng}`}
              className="hover:text-gray-300 transition-colors duration-200"
            >
              {t("globals.terms-conditions")}
            </a>
          </div>
        </motion.div>

      </div>
    </footer>
  );
};
